import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "../../utils/icons";
import { useAuthStore } from "../../store/useAuthStore";
import { getSocket, encode, decode } from "../../services/socket";
import { SOCKET_EVENTS } from "../../utils/constants";
import { storage } from "../../utils/storage";

const DEFAULT_AVATAR = "//img2.distributedresourcestorage.com/avatar/57268122/s?t=0";

const Skeleton = ({ width, height, radius }) => (
  <div
    className="skeleton-pulse"
    style={{ width, height, borderRadius: radius || 8, background: "var(--color-menu-item-hover)" }}
  />
);

const UserProfileModal = ({ show, onClose }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ winCount: 0, betCount: 0 });
  const [profit, setProfit] = useState(0);
  const [loading, setLoading] = useState(true);

  const name = user?.name || storage.getKey("name") || "Guest";
  const avatar = user?.avatar || storage.getKey("avatar") || DEFAULT_AVATAR;
  const uid = user?.uid || localStorage.getItem("uid");

  useEffect(() => {
    if (!show) { setLoading(true); return; }
    if (!uid) return;

    const s = getSocket();
    if (!s) { setLoading(false); return; }

    const handleUserInfo = (data) => {
      const decoded = decode(data);
      if (decoded?.status) {
        setProfit(decoded.profit || 0);
      }
      setLoading(false);
    };

    s.on(SOCKET_EVENTS.USER_INFO, handleUserInfo);
    s.emit(SOCKET_EVENTS.USER_INFO, encode({
      id: uid,
      coin: storage.getKey("coin") || localStorage.getItem("coin") || "",
      rate: null,
      game: "all",
      first: true,
    }));

    // Timeout fallback
    const timer = setTimeout(() => setLoading(false), 2000);

    return () => {
      s.off(SOCKET_EVENTS.USER_INFO, handleUserInfo);
      clearTimeout(timer);
    };
  }, [show, uid]);

  const handleUnverifiedClick = () => {
    onClose();
    navigate("/global-settings");
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            className="upm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Centered wrapper */}
          <div className="upm-center-wrapper" onClick={onClose}>
            <motion.div
              className="upm-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="upm-header">
                <span className="upm-title">USER PROFILE</span>
                <button className="upm-close" onClick={onClose}>
                  <X size={22} color="#fff" />
                </button>
              </div>

              {/* Body */}
              <div className="upm-body">
                {loading ? (
                  /* Skeleton */
                  <div className="upm-profile-section">
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                      <Skeleton width={100} height={100} radius="50%" />
                    </div>
                    <Skeleton width={120} height={20} radius={8} />
                    <div style={{ marginTop: 20 }}>
                      <Skeleton width="100%" height={120} radius={15} />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Avatar + Name */}
                    <div className="upm-profile-section">
                      <motion.div
                        className="upm-avatar-container"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                      >
                        <img
                          className="upm-avatar"
                          src={avatar}
                          alt="Profile"
                          onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                        />
                      </motion.div>
                      <motion.div
                        className="upm-name-row"
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.15, duration: 0.25 }}
                      >
                        <h3 className="upm-name">{name}</h3>
                        <span className="upm-badge upm-badge--unverified" onClick={handleUnverifiedClick}>
                          <img src="/assets/images/unverified-account.png" alt="" style={{ width: 24, objectFit: "contain" }} />
                          <span className="upm-badge-text">Unverified</span>
                        </span>
                      </motion.div>
                    </div>

                    {/* Statistics */}
                    <motion.div
                      className="upm-stats-section"
                      initial={{ y: 15, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.25 }}
                    >
                      <h4 className="upm-stats-title">Statistics</h4>
                      <div className="upm-stats-grid">
                        <div className="upm-stat-box">
                          <div className="upm-stat-label">Total Wins</div>
                          <div className="upm-stat-value">{stats.winCount}</div>
                        </div>
                        <div className="upm-stat-box">
                          <div className="upm-stat-label">Total Bets</div>
                          <div className="upm-stat-value">{stats.betCount}</div>
                        </div>
                        <div className="upm-stat-box">
                          <div className="upm-stat-label">Total Wagered</div>
                          <div className="upm-stat-value">{parseFloat(profit).toFixed(5)}</div>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserProfileModal;
